/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, Inject, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { format } from 'date-fns';
import { Observable, first, map, timer } from 'rxjs';

import { Language } from '../environments/environment.model';
import { AboutComponent } from './components/about/about.component';
import { ChatComponent } from './components/chat/chat.component';
import { CountDownComponent } from './components/countdown/countdown.component';
import { CutCardComponent } from './components/cut-card/cut-card.component';
import { GameSwitchComponent } from './components/game-switch/game-switch.component';
import { ModalComponent } from './components/modal/modal.component';
import { NotificationComponent } from './components/notification/notification.component';
import { StreamingVideoComponent } from './components/streaming-video/streaming-video.component';
import { GameSelectionComponent } from './components/supervisor/game-selection/game-selection.component';
import { ResultChangeComponent } from './components/supervisor/result-change/result-change.component';
import { SupervisorActionsComponent } from './components/supervisor/supervisor-actions/supervisor-actions.component';
import { SupervisorPanelsComponent } from './components/supervisor/supervisor-panels/supervisor-panels.component';
import { EnvironmentResolver } from './core/environment-resolver';
import { DealerServiceToken } from './factories/dealer.factory';
import {
    AppPhase,
    DealerAction,
    FlagStateType,
    GameType,
    NotificationCode,
    NotificationSeverity,
    SupervisorAction,
    SupervisorSubPhase,
    TableMode,
    WorkerAccessLevel
} from './models/communication/app-enums.model';
import {
    AppEventType,
    LoginChangeEvent,
    NewRoundPreparationPhaseChangeEvent,
    NotificationEvent
} from './models/communication/app-events.model';
import { AppState } from './models/communication/app-state.model';
import { CardsChanged } from './models/communication/result-change.model';
import { YEventPayload } from './models/y-protocol.model';
import { BetsStatusComponent } from './phases/bets-status/bets-status.component';
import { BurnCardsComponent } from './phases/burn-cards/burn-cards.component';
import { ErrorPanelComponent } from './phases/error-panel/error-panel.component';
import { NewRoundPreparationComponent } from './phases/new-round-preparation/new-round-preparation.component';
import { RoundVoidedComponent } from './phases/round-voided/round-voided.component';
import { ShoeChangeComponent } from './phases/shoe-change/shoe-change.component';
import { WaitingForCroupierComponent } from './phases/waiting-for-croupier/waiting-for-croupier.component';
import { WaitingForPlayersComponent } from './phases/waiting-for-players/waiting-for-players.component';
import { GameLabelPipe } from './pipes/game-label.pipe';
import { CountdownService } from './services/countdown.service';
import { DealerService } from './services/dealer.service';
import { NotificationService } from './services/notification.service';
import { PluginService } from './services/plugin.service';
import { ResultChange } from './services/result-change.service';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        BurnCardsComponent,
        WaitingForPlayersComponent,
        ShoeChangeComponent,
        NewRoundPreparationComponent,
        RoundVoidedComponent,
        ErrorPanelComponent,
        OverlayModule,
        AboutComponent,
        NotificationComponent,
        CutCardComponent,
        ModalComponent,
        BetsStatusComponent,
        GameSwitchComponent,
        GameLabelPipe,
        CountDownComponent,
        ChatComponent,
        StreamingVideoComponent,
        ResultChangeComponent,
        WaitingForCroupierComponent,
        SupervisorActionsComponent,
        GameSelectionComponent,
        SupervisorPanelsComponent
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent {
    private static readonly originalWidth = 1920;
    private static readonly originalHeight = 1080;
    private static readonly gameChangeDelayMs = 50;

    @ViewChild('container') containerEl!: ElementRef<HTMLElement>;

    protected viewportScaling = {
        enabled: true,
        fillWidth: true,
        fillHeight: true
    };

    protected containerHeight = AppComponent.originalHeight;
    protected containerWidth = AppComponent.originalWidth;

    protected appIsHidden = false;
    protected aboutDialogVisible = false;
    protected menuVisible = false;

    protected time$: Observable<string> = this.createTimeObservable();
    protected state$!: Observable<AppState>;
    protected version: string;
    protected languages: Language[] = [];

    protected isVideoHidden = false;
    protected cutCardDrawn = false;

    protected readonly AppPhase = AppPhase;
    protected readonly TableMode = TableMode;
    protected readonly WorkerAccessLevel = WorkerAccessLevel;
    protected readonly SupervisorSubPhase = SupervisorSubPhase;
    protected readonly SupervisorAction = SupervisorAction;
    protected readonly DealerAction = DealerAction;

    private buttonsDisabled = false;

    constructor(
        private readonly environment: EnvironmentResolver,
        private readonly route: ActivatedRoute,
        private readonly pluginService: PluginService,
        private readonly destroyRef: DestroyRef,
        private readonly countdown: CountdownService,
        protected readonly resultChange: ResultChange,
        protected readonly notification: NotificationService,
        @Inject(DealerServiceToken) protected readonly dealerService: DealerService
    ) {
        const { version, languages } = this.environment.getEnvironment();
        this.version = version;
        this.languages = languages || [];

        this.listenToDealerService();
        this.dealerService.start();
    }

    // SUPERVISOR

    protected handleSupervisorLogout(): void {
        if (this.isCountDownPresent()) {
            this.countdown.restart();
        }
    }

    protected async callSupervisor(): Promise<void> {
        if (this.isCountDownPresent()) {
            this.countdown.pause();
        }

        await this.sendDealerAction(DealerAction.CallSupervisor);
    }

    protected async selectGame(game: GameType): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendChangeGameRequest(game);
        });
    }

    protected async sendSupervisorAction(action: SupervisorAction): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendSupervisorActionRequest(action);
        });
    }

    protected async sendDealerAction(action: DealerAction): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendDealerActionRequest(action);
        });
    }

    protected async cancelSupervisorAction(): Promise<void> {
        const supervisorSubPhase = this.dealerService.getState().supervisorState?.subPhase;

        if (supervisorSubPhase === SupervisorSubPhase.WaitingForSupervisor) {
            // If the croupier cancels the `WaitingForSupervisor` sub phase in the cases
            // the countdown has started (and now being paused), we need to restart it.
            if (this.isCountDownPresent()) {
                this.countdown.restart();
            }
            // Other sub phases in which the supervisor is logged in
        } else if (this.dealerService.getState().flagsState.resultChangingRequested) {
            // Because we confirmed the result change which modified the cards,
            // we need to explicitly revert them to the original values.
            this.resultChange.revertCardValues();
        }

        await this.onButtonClick(async () => {
            await this.dealerService.sendCancelSupervisorActionRequest();
        });
    }

    protected async confirmResultChange(data: CardsChanged): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendResultChangeRequest(data);
        });
    }

    protected async toggleChat(toggle?: boolean): Promise<void> {
        await this.onButtonClick(async () => {
            await this.dealerService.sendSetFlagStateRequest(FlagStateType.ChatEnabled, !toggle);
        });
    }

    protected changeLanguage(languageCode: string): void {
        window.location.href = `/${languageCode}/${window.location.search}`;
    }

    protected scaleContainer(event: UIEvent): void {
        if (this.viewportScaling.enabled) {
            this.scaleViewport((event.target as Window).innerWidth, (event.target as Window).innerHeight);
        }
    }

    private handleNotification({ code, severity }: NotificationEvent): void {
        // Don't show a notification after scanning cards as
        // it temporarily covers which is annoying for the user.
        if (code === NotificationCode.AuthenticationSuccess) {
            return;
        }

        const showCloseButton = severity !== NotificationSeverity.Info;
        this.notification.show({ code, severity, showCloseButton });
    }

    private listenToDealerService(): void {
        this.state$ = this.dealerService.getStateObservable();
        this.state$.pipe(first()).subscribe(() => {
            // Timeout because - TypeError: Cannot read properties of undefined (reading 'nativeElement')
            if (this.viewportScaling.enabled) {
                setTimeout(() => {
                    this.scaleViewport(window.innerWidth, window.innerHeight);
                });
            }
        });

        this.dealerService
            .getEventsObservable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                this.handleDealerEvent(event);
                this.callPlugins(event);
            });
    }

    private handleDealerEvent(event: YEventPayload<AppEventType, unknown>): void {
        if (event.name === AppEventType.LoginChange) {
            if ((event.data as LoginChangeEvent).old?.accessLevel === WorkerAccessLevel.Supervisor) {
                this.handleSupervisorLogout();
            }
        } else if (event.name === AppEventType.CutCardDrawn) {
            this.cutCardDrawn = true;
            this.handleCutCardDrawnEvent();
        } else if (event.name === AppEventType.Notification) {
            this.handleNotification(event.data as NotificationEvent);
        } else if (
            this.cutCardDrawn &&
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            (event.data as NewRoundPreparationPhaseChangeEvent).phase === AppPhase.NewRoundPreparation
        ) {
            this.cutCardDrawn = false;
            this.notification.hide();
        }
    }

    private callPlugins(event: YEventPayload<AppEventType, unknown>): void {
        for (const plugin of this.pluginService.plugins) {
            plugin(this.dealerService.getState(), event, this.containerEl.nativeElement);
        }
    }

    private async onButtonClick(callback: () => Promise<void>): Promise<void> {
        if (this.buttonsDisabled) {
            return;
        }

        try {
            this.buttonsDisabled = true;
            await callback();
            this.buttonsDisabled = false;
        } catch (error) {
            console.error(error);
            this.buttonsDisabled = false;
            this.notification.show({ code: NotificationCode.ActionFailed, severity: NotificationSeverity.Error, showCloseButton: true });
        }
    }

    private createTimeObservable(): Observable<string> {
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        return timer(0, 1000).pipe(map(() => format(new Date(), 'HH:mm:ss')));
    }

    private handleCutCardDrawnEvent(): void {
        this.notification.show({ code: NotificationCode.CutCardDrawn, severity: NotificationSeverity.Error, showCloseButton: true });
    }

    private scaleViewport(innerWidth: number, innerHeight: number): void {
        const originalProportion = AppComponent.originalWidth / AppComponent.originalHeight;

        const windowProportion = innerWidth / innerHeight;
        const proportionRatio = windowProportion / originalProportion;

        const scale = proportionRatio < 1 ? innerWidth / AppComponent.originalWidth : innerHeight / AppComponent.originalHeight;

        if (this.viewportScaling.fillWidth) {
            this.containerWidth = Math.max(proportionRatio, 1) * AppComponent.originalWidth;
        }

        if (this.viewportScaling.fillHeight) {
            this.containerHeight = AppComponent.originalHeight + AppComponent.originalHeight * (1 - Math.min(proportionRatio, 1));
        }

        this.containerEl.nativeElement.style.transform = `scale(${scale})`;
        this.containerEl.nativeElement.style.width = `${this.containerWidth}px`;
        this.containerEl.nativeElement.style.height = `${this.containerHeight}px`;
    }

    private isCountDownPresent(): boolean {
        const { phase } = this.dealerService.getState().roundState;
        return phase === AppPhase.BetsOpen || phase === AppPhase.Decision;
    }
}
